import {useEffect, useEffectEvent, useRef} from 'react'
import {sendCustomMetric} from '@github-ui/stats'
import {useAnalytics} from '@github-ui/use-analytics'
import type {PullRequestUserActionStack} from '@github-ui/pull-requests/analytics/pull-request-user-action'

import type {PullRequestMergeRequirementsState} from '../types'

export const UNKNOWN_MERGEABILITY_THRESHOLD_MS = 10 * 60 * 1000

export const UNKNOWN_MERGEABILITY_RESOLVED_EVENT = 'merge_box.unknown_mergeability_resolved'
export const UNKNOWN_MERGEABILITY_THRESHOLD_EXCEEDED_EVENT = 'merge_box.unknown_mergeability_threshold_exceeded'

type UnknownMergeabilitySource = 'stacked_pull_request' | 'unstacked_pull_request'

type EventContext = Record<string, string | number>

type Timer = {startTime: number; exceededThreshold: boolean}

export type UnknownMergeabilityStackEntry = {pullRequestNumber: number; isUnknown: boolean}

type TimerState = {
  scope: string | undefined
  // Keys currently being timed.
  timers: Map<string, Timer>
}

// Joined with a newline so the key lists can be effect dependencies without changing on every render.
const KEY_SEPARATOR = '\n'

/**
 * Reports how long each stack entry's mergeability stays unknown.
 */
export function useStackedPullHasUnknownMergeabilityTelemetry({
  entries,
  stackMetadata,
  isRebasing,
}: {
  entries: UnknownMergeabilityStackEntry[]
  stackMetadata: PullRequestUserActionStack | undefined
  isRebasing: boolean
}) {
  useUnknownMergeabilityTimers({
    source: 'stacked_pull_request',
    scope: stackMetadata ? `${stackMetadata.id}` : undefined,
    memberKeys: entries.map(entry => `${entry.pullRequestNumber}`),
    unknownKeys: entries.filter(entry => entry.isUnknown).map(entry => `${entry.pullRequestNumber}`),
    // A rebase shows every entry as loading, regardless of its mergeability, so don't start timing then.
    canStartTiming: !isRebasing,
    contextFor: pullRequestNumber => ({
      stack_id: stackMetadata?.id ?? '',
      stack_number: stackMetadata?.number ?? '',
      pull_request_number: Number(pullRequestNumber),
    }),
  })
}

/**
 * Reports how long a pull request that isn't stacked has an `UNKNOWN` merge requirements state in the
 * `merge_box` response.
 */
export function useUnstackedPullHasUnknownMergeabilityTelemetry({
  pullRequestId,
  mergeRequirementsState,
  isStacked,
}: {
  pullRequestId: string | undefined
  mergeRequirementsState: PullRequestMergeRequirementsState | undefined
  isStacked: boolean
}) {
  useUnknownMergeabilityTimers({
    source: 'unstacked_pull_request',
    scope: pullRequestId,
    // Becoming stacked removes the pull request from this tracking without reporting it as resolved.
    memberKeys: isStacked || !pullRequestId ? [] : [pullRequestId],
    unknownKeys: !isStacked && pullRequestId && mergeRequirementsState === 'UNKNOWN' ? [pullRequestId] : [],
    canStartTiming: true,
    contextFor: id => ({pull_request_id: id}),
  })
}

/**
 * Times every period a key is unknown, from when it becomes unknown until it stops, and reports the duration. A key
 * can be unknown more than once, for example after a rebase on this page or another one. A separate report is sent
 * for any period still unknown after `UNKNOWN_MERGEABILITY_THRESHOLD_MS`, so stuck keys are reported even if they
 * never resolve. Reports go to Hydro (to find who is affected) and to Datadog (for trends). Keys that leave
 * `memberKeys` while unknown are dropped without a report.
 */
function useUnknownMergeabilityTimers({
  source,
  scope,
  memberKeys,
  unknownKeys,
  canStartTiming,
  contextFor,
}: {
  source: UnknownMergeabilitySource
  // Timing resets when this changes. Nothing is timed while it is undefined.
  scope: string | undefined
  memberKeys: string[]
  unknownKeys: string[]
  canStartTiming: boolean
  contextFor: (key: string) => EventContext
}) {
  const {sendAnalyticsEvent} = useAnalytics()
  const stateRef = useRef<TimerState>({scope: undefined, timers: new Map()})
  const members = memberKeys.join(KEY_SEPARATOR)
  const unknown = unknownKeys.join(KEY_SEPARATOR)

  const reportResolved = useEffectEvent((key: string, timer: Timer, endTime: number) => {
    const durationMs = endTime - timer.startTime
    sendAnalyticsEvent(UNKNOWN_MERGEABILITY_RESOLVED_EVENT, undefined, {
      source,
      ...contextFor(key),
      start_time: new Date(timer.startTime).toISOString(),
      end_time: new Date(endTime).toISOString(),
      duration_ms: durationMs,
      exceeded_threshold: timer.exceededThreshold,
    })
    sendCustomMetric({
      name: 'PULL_REQUESTS_MERGE_BOX_UNKNOWN_MERGEABILITY_DURATION',
      value: durationMs,
      tags: {source, exceeded_threshold: `${timer.exceededThreshold}`},
    })
  })

  const reportThresholdExceeded = useEffectEvent((key: string, timer: Timer) => {
    sendAnalyticsEvent(UNKNOWN_MERGEABILITY_THRESHOLD_EXCEEDED_EVENT, undefined, {
      source,
      ...contextFor(key),
      start_time: new Date(timer.startTime).toISOString(),
      threshold_ms: UNKNOWN_MERGEABILITY_THRESHOLD_MS,
    })
    sendCustomMetric({
      name: 'PULL_REQUESTS_MERGE_BOX_UNKNOWN_MERGEABILITY_THRESHOLD_EXCEEDED',
      value: 1,
      tags: {source},
    })
  })

  useEffect(() => {
    reconcileTimers(stateRef.current, {
      scope,
      members: splitKeys(members),
      unknown: splitKeys(unknown),
      canStartTiming,
      now: Date.now(),
      onResolved: reportResolved,
      onThresholdExceeded: reportThresholdExceeded,
    })
  }, [scope, members, unknown, canStartTiming])

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const reportExceeded = () => {
      const nextDeadline = reportExceededThresholds(stateRef.current, Date.now(), reportThresholdExceeded)
      if (nextDeadline !== undefined) timeoutId = setTimeout(reportExceeded, nextDeadline - Date.now())
    }
    reportExceeded()
    return () => clearTimeout(timeoutId)
  }, [scope, unknown, canStartTiming])
}

function reconcileTimers(
  state: TimerState,
  {
    scope,
    members,
    unknown,
    canStartTiming,
    now,
    onResolved,
    onThresholdExceeded,
  }: {
    scope: string | undefined
    members: string[]
    unknown: string[]
    canStartTiming: boolean
    now: number
    onResolved: (key: string, timer: Timer, endTime: number) => void
    onThresholdExceeded: (key: string, timer: Timer) => void
  },
) {
  if (state.scope !== scope) {
    state.timers.clear()
    state.scope = scope
  }
  if (scope === undefined) return

  const memberSet = new Set(members)
  const unknownSet = new Set(unknown)

  for (const [key, timer] of state.timers) {
    if (unknownSet.has(key)) continue
    state.timers.delete(key)
    if (!memberSet.has(key)) continue
    // Browsers delay timers in hidden tabs, so the period can end after the threshold but before its timeout runs.
    if (!timer.exceededThreshold && now - timer.startTime >= UNKNOWN_MERGEABILITY_THRESHOLD_MS) {
      timer.exceededThreshold = true
      onThresholdExceeded(key, timer)
    }
    onResolved(key, timer, now)
  }

  if (!canStartTiming) return

  for (const key of unknownSet) {
    if (state.timers.has(key)) continue
    state.timers.set(key, {startTime: now, exceededThreshold: false})
  }
}

// Reports every timer that has passed the threshold, and returns when the next one will.
function reportExceededThresholds(
  state: TimerState,
  now: number,
  onThresholdExceeded: (key: string, timer: Timer) => void,
): number | undefined {
  let nextDeadline: number | undefined
  for (const [key, timer] of state.timers) {
    if (timer.exceededThreshold) continue
    const deadline = timer.startTime + UNKNOWN_MERGEABILITY_THRESHOLD_MS
    if (deadline > now) {
      nextDeadline = nextDeadline === undefined ? deadline : Math.min(nextDeadline, deadline)
      continue
    }
    timer.exceededThreshold = true
    onThresholdExceeded(key, timer)
  }
  return nextDeadline
}

function splitKeys(value: string): string[] {
  return value ? value.split(KEY_SEPARATOR) : []
}
